import React from "react";
import { Box } from "@mui/material";
import { FormGridContext } from "./shared/formGridContext";

/**
 * FormGrid — lays out Rff* fields in a responsive CSS grid (default 12 columns)
 * with automatic gap spacing. This is the replacement for the old
 * `<Grid container spacing>` parent.
 *
 * Each child field expresses its width via its `size` prop (columns to span,
 * 1–12) and collapses to full width below the `sm` breakpoint. Unlike MUI
 * <Grid>, there is no negative-margin wrapper and the fields stay layout-
 * agnostic: drop them anywhere and they stack full-width; place them inside a
 * FormGrid and they flow into columns.
 *
 * Props:
 *   columns — number of grid columns the row is divided into (default 12)
 *   gap — MUI spacing units between cells (default 2.5 = 20px)
 *   rowGap / columnGap — override one axis; both fall back to `gap`. Rows often
 *     want more air than columns, since a row gap separates whole
 *     label-plus-control units while a column gap only separates two adjacent
 *     controls.
 *   sx — escape hatch for the grid container
 */
const FormGrid = ({
  columns = 12,
  gap = 2.5,
  rowGap,
  columnGap,
  sx,
  children,
}) => (
  <FormGridContext.Provider value={{ active: true, columns }}>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        alignItems: "start",
        rowGap: rowGap ?? gap,
        columnGap: columnGap ?? gap,
        ...sx,
      }}
    >
      {children}
    </Box>
  </FormGridContext.Provider>
);

export default FormGrid;
